import { useState } from 'react';

/** Pre-auth screens — the only routes a session may render and still keep its tree. */
const PRE_AUTH_PATHS = new Set(['/', '/reset-password', '/accept-invite']);
/**
 * A DIFFERENT user arriving after the kept sign-out remounts here (RD-SEC-036).
 * Not /accept-invite: its "sign out and continue" (KAN-560) is exactly A → out
 * → B, and a remount there loses the link's tenant/context — that page binds
 * its form to the verified user and re-checks the live session at submit.
 */
const LINK_PAGE_PATHS = new Set(['/reset-password']);

/**
 * Pure transition for the session-scoped routes tree key (App.js
 * `KeyedRoutesAndAi`). The tree remounts so no React state from one user can
 * survive into another's session — but only a transition AWAY from a user
 * that has been somewhere needs that:
 *
 *   signed out → user   SAME epoch — the signed-out tree was mounted fresh at
 *                       boot or at sign-out, so it holds no user's state; it
 *                       is the Login page mid-choreography.
 *   user → signed out   new epoch, UNLESS this session never left the
 *                       pre-auth screens (`preAuthOnly`): backing out of the
 *                       2FA step, a failed post-sign-in check, the reset /
 *                       invite page signing its one-time session out after
 *                       success. That tree only ever rendered those screens
 *                       (useAiAssistant resets on any userId change), so
 *                       keeping it lets the step slide back — and keeps the
 *                       reset page's "done" screen instead of remounting it
 *                       onto a spent link ("This link has expired", 2026-09-25).
 *   user A → user B     new epoch, always — including A → signed out → B on
 *                       the reset page (`signedOutUserId`, RD-SEC-036):
 *                       BootGate no longer blanks those pages while a new
 *                       user's language loads, and that blank used to be what
 *                       remounted them when another tab swapped accounts.
 *
 * `preAuthOnly` is set when a session starts ON a pre-auth screen and is
 * cleared for good the first render that session is seen anywhere else — it
 * can only ever go true → false within a session, so it fails toward
 * remounting.
 *
 * Why the sign-in row exists (2026-09-25): keying on `sessionUser?.id ?? 'anon'`
 * remounted Login the instant SIGNED_IN landed — before signInWithPassword
 * even resolved — so the green "Done" beat and the wordmark → splash morph
 * ran on an unmounted instance and were never seen.
 */
export function nextSessionTree(prev, userId, pathname) {
  const id = userId ?? null;
  const onPreAuth = PRE_AUTH_PATHS.has(pathname);
  if (prev.userId === id) {
    return id !== null && prev.preAuthOnly && !onPreAuth ? { ...prev, preAuthOnly: false } : prev;
  }
  if (prev.userId === null) {
    const swapped = prev.signedOutUserId != null && id !== prev.signedOutUserId && LINK_PAGE_PATHS.has(pathname);
    return { userId: id, epoch: swapped ? prev.epoch + 1 : prev.epoch, preAuthOnly: onPreAuth };
  }
  if (id === null && prev.preAuthOnly && onPreAuth) {
    return { userId: null, epoch: prev.epoch, preAuthOnly: false, signedOutUserId: prev.userId };
  }
  return { userId: id, epoch: prev.epoch + 1, preAuthOnly: id !== null && onPreAuth };
}

/** Stable key for the session-scoped tree; see nextSessionTree. */
export default function useSessionTreeKey(userId, pathname) {
  const [tree, setTree] = useState(() => ({
    userId: userId ?? null, epoch: 0, preAuthOnly: userId != null && PRE_AUTH_PATHS.has(pathname),
  }));
  const next = nextSessionTree(tree, userId, pathname);
  // Store-previous-render pattern: React re-renders this component before
  // committing, and the key below already uses `next`, so a user switch never
  // paints even one frame of the old tree.
  if (next !== tree) setTree(next);
  return `session-${next.epoch}`;
}
